import { Container, TextField, Typography, Box, Grid, Chip, Avatar, InputAdornment, Paper } from '@material-ui/core';
import React, { useState, useMemo } from 'react';
import {
  CardEmployee,
  ContainerButtons,
  Content,
  DivEmployeer,
  DivSearch,
  Main
} from './styles';
import { Button } from '@components/Button';
import {
  MagnifyingGlass,
  Trash,
  User,
  UserPlus,
  X
} from '@phosphor-icons/react';
import { Pen } from '@phosphor-icons/react/dist/ssr';
import { ModalEditarFuncionario } from './components/ModalEditarFuncionario';
import { ModalDeletarFuncionario } from './components/ModalDeletarFuncionario';
import {
  CadastroFuncionarioFormData,
  ModalCadastrarFuncionario
} from './components/ModalCadastrarFuncionario';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { getFuncionarios } from '@api/get-funcionarios';

import { Button as MuiButton } from '@material-ui/core';

const searchSchemaFormData = z.object({
  funcionario: z
    .string()
    .min(3, { message: 'Digite no mínimo 3 caracteres.' })
    .optional(),
  cod_contrato: z.string().optional()
});

type SearchEmployeerProps = z.infer<typeof searchSchemaFormData>;

export function Funcionarios() {
  const [openModalEdit, setOpenModalEdit] = useState(false);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [openModalCadastro, setOpenModalCadastro] = useState(false);
  const [editEmployee, setEditEmployee] = useState<CadastroFuncionarioFormData | null>(null);
  const [deleteEmployee, setDeleteEmployee] = useState(
    {} as CadastroFuncionarioFormData
  );
  const [searchTerm, setSearchTerm] = useState('');

  const {
    handleSubmit,
    register,
    reset,
    formState: { errors, isSubmitting, isValid }
  } = useForm<SearchEmployeerProps>({
    resolver: zodResolver(searchSchemaFormData)
  });

  const {
    data: getDataEmployee,
    isLoading,
    isFetching
  } = useQuery({
    queryKey: ['getUserAdm'],
    queryFn: getFuncionarios
  });

  // Função para filtrar funcionários com base no termo de pesquisa
  const filteredEmployees = useMemo(() => {
    if (!getDataEmployee) return [];
    
    if (!searchTerm.trim()) return getDataEmployee;
    
    const term = searchTerm.toLowerCase().trim();
    
    return getDataEmployee.filter(employee => 
      employee.nome?.toLowerCase().includes(term) ||
      employee.cpf?.includes(term) ||
      employee.tipo_login?.toLowerCase().includes(term) ||
      (employee.idLogin && employee.idLogin.toString().includes(term))
    );
  }, [getDataEmployee, searchTerm]);

  function handleCloseModalDelete() {
    setOpenModalDelete(false);
  }

  function handleCloseModalEdit() {
    setOpenModalEdit(false);
  }

  function handleCloseModalCadastro() {
    setOpenModalCadastro(false);
  }

  async function handleOpenModalEdit(index: number) {
    if (!filteredEmployees) return;

    const employee = filteredEmployees[index];

    const idLoginValue = employee.idLogin ? String(employee.idLogin) : "";

    setEditEmployee({
      ...employee,
      idLogin: idLoginValue,
    });

    setOpenModalEdit(true);
  }

  function handleOpenModalDelete(index: number) {
    if (!filteredEmployees) return;
    setOpenModalDelete(true);
    setDeleteEmployee(filteredEmployees[index]);
  }

  function handleOpenModalCadastro() {
    setOpenModalCadastro(true);
  }

  function handleSearchEmployee(data: SearchEmployeerProps) {
    setSearchTerm(data.funcionario || '');
  }

  function handleClearSearch() {
    reset();
    setSearchTerm('');
  }

  return (
    <Container maxWidth="xl" style={{ padding: '24px 16px', minHeight: '100vh' }}>
      <Box display="flex" flexDirection="column" style={{ gap: '24px' }}>
        <Typography
          variant="h4"
          style={{ 
            fontWeight: 'bold',
            color: '#2c3e50',
            marginBottom: '16px'
          }}
        >
          Gerenciamento de Funcionários
        </Typography>
        
        <Paper elevation={1} style={{ padding: '24px', borderRadius: '12px' }}>
          <Box 
            display="flex" 
            justifyContent="space-between" 
            alignItems="flex-start" 
            flexWrap="wrap" 
            style={{ gap: '20px', marginBottom: '24px' }}
          >
            <Box 
              component="form" 
              onSubmit={handleSubmit(handleSearchEmployee)}
              style={{ flexGrow: 1, minWidth: '300px', maxWidth: '600px' }}
            >
              <Typography variant="subtitle1" style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                Pesquisar Funcionários
              </Typography>
              <Box display="flex" style={{ gap: '12px' }} alignItems="flex-start">
                <TextField
                  variant="outlined"
                  label="Nome do funcionário"
                  fullWidth
                  size="medium"
                  {...register('funcionario')}
                  error={!!errors.funcionario}
                  helperText={errors.funcionario?.message}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {/* CORREÇÃO DO ÍCONE - Verifique se o ícone está sendo importado corretamente */}
                        <MagnifyingGlass size={22} weight="bold" />
                      </InputAdornment>
                    ),
                  }}
                  style={{ backgroundColor: 'white' }}
                />
                <Box display="flex" style={{ gap: '8px' }} alignItems="center">
<MuiButton
  type="submit"
  variant="contained"
  color="secondary"
  disabled={!isValid || isSubmitting}
  style={{ minWidth: '100px', height: '48px' }}
  startIcon={<MagnifyingGlass size={18} weight="bold" />}
>
  {isSubmitting ? 'Buscando...' : 'Buscar'}
</MuiButton>
                  {searchTerm && (
                    <Button
                      icon={<X size={20} />}
                      variantstyle="RED"
                      onClick={handleClearSearch}
                      style={{ minWidth: '48px', height: '48px' }}
                    />
                  )}
                </Box>
              </Box>
            </Box>
            
            <Box style={{ textAlign: 'right' }}>
              <Typography variant="subtitle1" style={{ fontWeight: 'bold', marginBottom: '8px', visibility: 'hidden' }}>
                Ações
              </Typography>
              <Button
                icon={<UserPlus size={22} />}
                onClick={handleOpenModalCadastro}
                variantstyle="PRIMARY"
                style={{ height: '48px', padding: '0 16px' }}
              >
                Novo Funcionário
              </Button>
            </Box>
          </Box>
          
          <Box style={{ marginTop: '32px' }}>
            <Typography variant="h6" style={{ marginBottom: '16px', fontWeight: 'bold' }}>
              {filteredEmployees ? `Total: ${filteredEmployees.length} funcionário(s)` : 'Carregando...'}
              {searchTerm && ` (Filtrado)`}
            </Typography>
            
            {filteredEmployees && filteredEmployees.length > 0 ? (
              <Grid container spacing={3}>
                {filteredEmployees.map((employee, index) => {
                  const isAdmin = String(employee.administrador).toLowerCase() === 'sim';
                  
                  return (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                      <Paper 
                        elevation={2} 
                        style={{ 
                          padding: '20px', 
                          borderRadius: '12px',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between'
                        }}
                      >
                        <Box>
                          <Box display="flex" alignItems="center" style={{ gap: '16px', marginBottom: '16px' }}>
                            <Avatar style={{ 
                              backgroundColor: isAdmin ? '#1976d2' : '#4caf50',
                              width: '50px',
                              height: '50px'
                            }}>
                              <User size={24} />
                            </Avatar>
                            <Box>
                              <Typography variant="h6" style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                                {employee.nome}
                              </Typography>
                              <Typography variant="body2" color="textSecondary" style={{ marginTop: '4px' }}>
                                {employee.tipo_login}
                              </Typography>
                            </Box>
                          </Box>
                          
                          <Box style={{ marginBottom: '20px' }}>
                            <Chip 
                              label={isAdmin ? 'ADMINISTRADOR' : 'USUÁRIO NORMAL'} 
                              size="medium"
                              color={isAdmin ? "primary" : "default"}
                              style={{ 
                                fontWeight: 'bold',
                                backgroundColor: isAdmin ? '#1976d2' : '#f5f5f5',
                                color: isAdmin ? 'white' : 'rgba(0, 0, 0, 0.87)'
                              }}
                            />
                          </Box>
                        </Box>
                        
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" color="textSecondary" style={{ fontStyle: 'italic' }}>
                            CPF: {employee.cpf}
                          </Typography>
                          
                          <Box display="flex" style={{ gap: '8px' }}>
                            <Button
                              icon={<Pen size={18} />}
                              variantstyle="SECUNDARY"
                              type="button"
                              isLoading={isLoading}
                              onClick={async () => {
                                await handleOpenModalEdit(index);
                              }}
                              style={{ minWidth: '44px', height: '44px' }}
                            />
                            <Button
                              icon={<Trash size={18} />}
                              variantstyle="RED"
                              type="button"
                              onClick={() => {
                                handleOpenModalDelete(index);
                              }}
                              isLoading={
                                deleteEmployee.cpf === employee.cpf ? isFetching : false
                              }
                              style={{ minWidth: '44px', height: '44px' }}
                            />
                          </Box>
                        </Box>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            ) : (
              <Box 
                display="flex" 
                flexDirection="column" 
                alignItems="center" 
                justifyContent="center" 
                style={{ padding: '60px 0' }}
                textAlign="center"
              >
                <User size={64} color="#ddd" />
                <Typography variant="h6" color="textSecondary" style={{ marginTop: '20px', fontWeight: 'bold' }}>
                  {searchTerm ? 'Nenhum funcionário encontrado' : 'Nenhum funcionário cadastrado'}
                </Typography>
                <Typography variant="body1" color="textSecondary" style={{ marginTop: '12px' }}>
                  {searchTerm ? 'Tente ajustar os termos de busca ou adicione um novo funcionário.' : 'Adicione um novo funcionário para começar.'}
                </Typography>
                <Button
                  icon={<UserPlus size={20} />}
                  onClick={handleOpenModalCadastro}
                  variantstyle="PRIMARY"
                  style={{ marginTop: '20px' }}
                >
                  Adicionar Funcionário
                </Button>
              </Box>
            )}
          </Box>
        </Paper>
      </Box>
      
      <ModalCadastrarFuncionario
        openModal={openModalCadastro}
        closeModal={handleCloseModalCadastro}
      />
      
      {editEmployee && (
        <ModalEditarFuncionario
          openModal={openModalEdit}
          closeModal={handleCloseModalEdit}
          data={editEmployee}
        />
      )}
      
      <ModalDeletarFuncionario
        openModal={openModalDelete}
        closeModal={handleCloseModalDelete}
        data={deleteEmployee}
      />
    </Container>
  );
}